'use client'

import { startTransition } from 'react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel, FieldSeparator } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import GoogleSignInButton from './GoogleSignInButton'
import AppleLoginButton from './AppleLoginButton'
import { PlaytomicLogo } from '@/components/PlaytomicLogo'

export type LoginFormProps = {
  state: { email?: string; password?: string; error?: string }
  formAction: (formData: FormData) => void | Promise<void>
  isPending: boolean

  /** Configuration for social-login providers (Apple, Google, Facebook). */
  socialLoginConfig?: {
    /** Configuration for Google login. If not provided, Google login will not be rendered. */
    google?: { clientId: string }

    /** Configuration for Facebook login. If not provided, Facebook login will not be rendered. */
    facebook?: { appId: string }

    /** Configuration for Apple login. If not provided, Apple login will not be rendered. */
    apple?: { clientId: string }
  }
}

export default function LoginForm({
  state: loginState,
  formAction,
  isPending: loginInProgress,
  socialLoginConfig,
}: LoginFormProps) {
  const t = useTranslations('auth.login.form')

  /**
   * 3rd-party don't support being disabled because the are rendered as iframes.
   *
   * This is a workaround to give feedback to the user and minimize the chances of them
   * trying to click a 3rd-party login button while the login is in progress.
   */
  const renderWithLoadingBlanket = (element: React.ReactElement) => {
    return (
      <div className="relative flex rounded">
        {loginInProgress ? (
          <div className="absolute inset-0 flex items-center">
            <div className="z-10 h-full w-full rounded bg-gray-300 opacity-50"></div>
          </div>
        ) : null}
        {element}
      </div>
    )
  }

  return (
    <div className="flex w-full max-w-xs flex-col gap-6 pb-4">
      {(socialLoginConfig?.apple?.clientId ||
        socialLoginConfig?.google?.clientId ||
        socialLoginConfig?.facebook?.appId) && (
        <>
          <div className="flex flex-col gap-4">
            {/* Apple login */}
            {socialLoginConfig?.apple?.clientId &&
              renderWithLoadingBlanket(
                <div className="min-h-[40px] w-full">
                  <AppleLoginButton
                    onSignIn={(credentials) =>
                      startTransition(() => {
                        console.debug('Apple sign-in success', credentials)

                        const formData = new FormData()
                        formData.set('type', 'apple')
                        formData.set('token', credentials.id_token)
                        // Note:
                        // Apple provides a name or an email **only for the first sign-in**
                        // Subsequent logins **will not** include them.
                        formData.set(
                          'name',
                          credentials.user?.name
                            ? `${credentials.user.name.firstName} ${credentials.user.name.lastName}`
                            : ''
                        )
                        formData.set('email', credentials.user?.email ?? '')
                        formAction(formData)
                      })
                    }
                  />
                </div>
              )}

            {/* Google login */}
            {socialLoginConfig?.google?.clientId &&
              renderWithLoadingBlanket(
                <div className="max-h-[40px] min-h-[40px] w-full">
                  <GoogleSignInButton
                    onCredentialsResponse={(credentials) =>
                      startTransition(() => {
                        console.debug('Google sign-in success', credentials)

                        const formData = new FormData()
                        formData.set('type', 'google')
                        formData.set('token', credentials.token)
                        formData.set('name', credentials.name)
                        formData.set('email', credentials.email)
                        formAction(formData)
                      })
                    }
                  />
                </div>
              )}

            {/* Facebook login - Coming soon */}
            {socialLoginConfig?.facebook?.appId && (
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  disabled
                  className="flex h-[40px] w-[320px] cursor-not-allowed items-center justify-center gap-2 rounded-md bg-[#1877F2] px-4 text-sm font-medium text-white opacity-60"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  {t('facebook')}
                </button>
                <span className="text-muted-foreground mt-1 text-xs">{t('comingSoon')}</span>
              </div>
            )}
          </div>

          {/* Separator */}
          <FieldSeparator>{t('separator')}</FieldSeparator>
        </>
      )}

      <form action={formAction} className="flex w-full flex-col gap-6">
        {!loginInProgress && loginState?.error && (
          <div className="text-destructive text-center text-sm">{loginState.error}</div>
        )}

        <input type="hidden" name="type" value="credentials" />

        <Field>
          <FieldLabel htmlFor="email">{t('email.label')}</FieldLabel>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            defaultValue={loginState?.email}
            required={true}
            placeholder={t('email.placeholder')}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="password">{t('password.label')}</FieldLabel>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required={true}
            placeholder={t('password.placeholder')}
          />
        </Field>

        <Button type="submit" className="bg-energy-blue hover:bg-energy-blue/90 mt-2 w-full" disabled={loginInProgress}>
          <PlaytomicLogo hideText={true} className="fill-white" />
          {t('submit')}
        </Button>

        <p className="text-muted-foreground text-center text-xs">{t('playtomicAccountRequired')}</p>
      </form>
    </div>
  )
}
